import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { ApiError } from "../../../api/client";
import type { Entity } from "../../../api/types";
import type { Family } from "../../../domain/families";
import { FamilyMappingCard } from "../../hub/mapping/FamilyMappingCard";
import { enabledFamiliesOf } from "../../hub/mapping/profileState";
import { startProfile, useEntityProfiles } from "../../hub/mapping/useProfileVersion";

/** One card per enabled family: its profile's state, coverage and versions, or a way to start one. */
export function MappingTab({ entity }: { entity: Entity }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const profiles = useEntityProfiles(entity.id);
  const [busy, setBusy] = useState<Family | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Family, string>>>({});

  async function create(family: Family): Promise<void> {
    setBusy(family);
    setErrors((prev) => ({ ...prev, [family]: "" }));
    try {
      await startProfile(entity.id, family);
    } catch (exc) {
      // Someone else made it first: the refreshed list shows it, which is what the click wanted.
      if (!(exc instanceof ApiError && exc.code === "PROFILE_EXISTS")) {
        const message =
          exc instanceof ApiError ? exc.message : t("companies.mapping.createError", "The profile could not be created.");
        setErrors((prev) => ({ ...prev, [family]: message }));
      }
    } finally {
      await queryClient.invalidateQueries({ queryKey: ["entities", entity.id, "profiles"] });
      setBusy(null);
    }
  }

  if (profiles.isPending) return null;

  return (
    <div className="grid">
      {enabledFamiliesOf(entity.enabledDocTypes).map((family) => (
        <FamilyMappingCard
          key={family}
          entityId={entity.id}
          family={family}
          profile={profiles.data?.find((item) => item.family === family)}
          busy={busy === family}
          error={errors[family] ?? ""}
          onCreate={(next) => void create(next)}
        />
      ))}
    </div>
  );
}
